import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Popover,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface StatsWidgetProps {
  title: string;
  value: string | number;
  change: string | null;
  dateRange: { start: Date; end: Date };
  infoText?: string;
}

export const StatsWidget: React.FC<StatsWidgetProps> = ({
  title,
  value,
  change,
  dateRange,
  infoText,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'info-popover' : undefined;

  const isPositive = change ? change.startsWith('+') : false;
  const changeColor = change === '0%' ? 'text.secondary' : (isPositive ? 'success.main' : 'error.main');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(8px)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.shadows[4],
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        {infoText && (
          <>
            <IconButton
              size="small"
              onClick={handleClick}
              sx={{ p: 0, ml: 1 }}
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
            </IconButton>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ p: 2, maxWidth: 300 }}>
                <Typography variant="body2">{infoText}</Typography>
              </Box>
            </Popover>
          </>
        )}
      </Box>
      <Typography variant="h4" component="div" sx={{ mb: 1 }}>
        {value}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
        {change === '0%' ? null : isPositive ? (
          <TrendingUpIcon sx={{ color: changeColor, mr: 0.5 }} />
        ) : (
          <TrendingDownIcon sx={{ color: changeColor, mr: 0.5 }} />
        )}
        <Typography variant="body2" sx={{ color: changeColor }}>
          {change}
        </Typography>
      </Box>
    </Paper>
  );
}; 